import { createApi } from '@reduxjs/toolkit/query/react';
import { apiQuery } from 'core/rtkQuery';
import {
  ActionsDto,
  ActionsSummaryDto,
  ActionsItemAlertsDto,
  CreateNotificationDto,
} from 'apis/backendApi';
import { addQueryContext, WithQueryContext } from 'core/apiQueryContext';
import itemApi from 'views/Maintain/redux/itemApi';

const actionsApiDef = createApi({
  reducerPath: 'actionsApi',
  baseQuery: apiQuery(),
  refetchOnMountOrArgChange: 5,

  endpoints: builder => ({
    getActionsSummary: builder.query<ActionsSummaryDto, WithQueryContext<{}>>({
      query: ({ buCode }) => ({
        url: '/actions/v1/{buCode}/summary',
        pathVariables: { buCode },
      }),
      // Summary is fetched explicitly, so does not need to be cached.
    }),

    getActions: builder.query<ActionsDto, WithQueryContext<{ divId: string }>>({
      query: ({ buCode, divId }) => ({
        url: '/actions/v1/{buCode}/division/{divId}',
        pathVariables: { buCode, divId },
      }),
    }),
    getActionsByItem: builder.query<
      ActionsItemAlertsDto,
      WithQueryContext<{ itemNo: string }>
    >({
      query: ({ buCode, itemNo }) => ({
        url: '/actions/v1/{buCode}/item/{itemNo}',
        pathVariables: { buCode, itemNo },
      }),
    }),

    putSlidNotificationStatus: builder.mutation<
      void,
      WithQueryContext<{ itemNo: string; slid: string }>
    >({
      query: ({ buCode, itemNo, slid }) => ({
        method: 'PUT',
        url: '/actions/v1/{buCode}/item/{itemNo}/notifications/status',
        pathVariables: { buCode, itemNo },
        body: {
          slid,
        },
      }),

      async onQueryStarted(
        { buCode, itemNo, slid },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
        } catch (e) {
          // Handled by caller. No throws in onQueryStarted as per RtkQuery
          return;
        }

        const { updateQueryData } = itemApi.util;
        dispatch(
          updateQueryData('getItemDetails', { buCode, itemNo }, draft => {
            draft.notifications = draft.notifications.filter(
              x => x.slid !== slid
            );
          })
        );
      },
    }),

    putUUIDNotificationStatus: builder.mutation<
      void,
      WithQueryContext<{ itemNo: string; id: string }>
    >({
      query: ({ buCode, itemNo, id }) => ({
        method: 'PUT',
        url: '/actions/v1/{buCode}/item/{itemNo}/notifications/status',
        pathVariables: { buCode, itemNo },
        body: {
          notificationIds: [id],
        },
      }),

      async onQueryStarted(
        { buCode, itemNo, id },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
        } catch (e) {
          // Handled by caller. No throws in onQueryStarted as per RtkQuery
          return;
        }

        const { updateQueryData } = itemApi.util;
        dispatch(
          updateQueryData('getItemDetails', { buCode, itemNo }, draft => {
            draft.notifications = draft.notifications.filter(x => x.id !== id);
          })
        );
      },
    }),

    putUUIDNotificationStatusMultiple: builder.mutation<
      void,
      WithQueryContext<{ itemNo: string; ids: string[] }>
    >({
      query: ({ buCode, itemNo, ids }) => ({
        method: 'PUT',
        url: '/actions/v1/{buCode}/item/{itemNo}/notifications/status',
        pathVariables: { buCode, itemNo },
        body: {
          notificationIds: ids,
        },
      }),

      async onQueryStarted(
        { buCode, itemNo, ids },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
        } catch (e) {
          // Handled by caller. No throws in onQueryStarted as per RtkQuery
          return;
        }

        const { updateQueryData } = itemApi.util;
        dispatch(
          updateQueryData('getItemDetails', { buCode, itemNo }, draft => {
            draft.notifications = draft.notifications.filter(
              x => !ids.includes(x.id)
            );
          })
        );
      },
    }),

    postNotification: builder.mutation<
      void,
      WithQueryContext<{ itemNo: string; notification: CreateNotificationDto }>
    >({
      query: ({ buCode, itemNo, notification }) => ({
        method: 'POST',
        url: '/actions/v1/{buCode}/item/{itemNo}/notifications',
        pathVariables: { buCode, itemNo },
        body: notification,
      }),
    }),

    deleteNotification: builder.mutation<
      void,
      WithQueryContext<{ itemNo: string; notificationId: string }>
    >({
      query: ({ buCode, itemNo, notificationId }) => ({
        method: 'DELETE',
        url: '/actions/v1/{buCode}/item/{itemNo}/notifications/{notificationId}',
        pathVariables: { buCode, itemNo, notificationId },
      }),
    }),
  }),
});
export const actionsApi = addQueryContext(actionsApiDef);
export default actionsApi;

export const {
  useGetActionsSummaryQuery,
  useGetActionsQuery,
  useGetActionsByItemQuery,
  usePutSlidNotificationStatusMutation,
  usePutUUIDNotificationStatusMutation,
  usePutUUIDNotificationStatusMultipleMutation,
  usePostNotificationMutation,
  useDeleteNotificationMutation,
} = actionsApi;
